import { ethers } from "ethers";
import PanSeaCollectionFactory from "./abis/PanSeaCollectionFactory.json";
import PanSeaERC1155Collection from "./abis/PanSeaERC1155Collection.json";
import PanSeaERC721Collection from "./abis/PanSeaERC721Collection.json";
import PanSeaERC721Drop from "./abis/PanSeaERC721Drop.json";
import PanSeaMarketplace from "./abis/PanSeaMarketplace.json";
import TestUSD from "./abis/TestUSD.json";

// Every custom error the v2 contracts can revert with, from all their ABIs.
const errorFragments = [
  PanSeaCollectionFactory,
  PanSeaERC1155Collection,
  PanSeaERC721Collection,
  PanSeaERC721Drop,
  PanSeaMarketplace,
  TestUSD,
]
  .flat()
  .filter((item) => item.type === "error");
const seen = new Set();
const contractErrors = new ethers.utils.Interface(
  errorFragments.filter((item) => {
    const key = `${item.name}(${item.inputs.map((input) => input.type).join(",")})`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }),
);

// User-facing messages, from pansea-new-contracts/docs/INTEGRATION.md §5.
const MESSAGES = {
  WrongPayment: "The amount sent does not match the price.",
  PriceMismatch: "The price changed. Please review and try again.",
  ListingNotActive: "This listing was already sold, cancelled or is no longer valid.",
  OfferNotActive: "This offer was already accepted or cancelled.",
  Expired: "This listing or offer has expired.",
  InvalidExpiry: "Choose an expiry between now and 365 days from now.",
  SelfTrade: "You can't buy your own listing or accept your own offer.",
  NotTokenOwner: "Your wallet doesn't hold enough of this NFT.",
  NotApproved: "The marketplace isn't approved to transfer this NFT.",
  InvalidQuantity: "Invalid quantity.",
  InvalidPrice: "Invalid price.",
  CurrencyNotAllowed: "That payment token isn't accepted.",
  UnsupportedNFT: "This contract isn't an ERC-721 or ERC-1155 NFT.",
  EnforcedPause: "This is paused for maintenance. Please try again later.",
  NotWhitelisted: "This drop is whitelist-only right now, and your wallet isn't on the list.",
  WalletLimitExceeded: "You've reached this drop's mint limit per wallet.",
  MaxSupplyExceeded: "Sold out: this would exceed the maximum supply.",
  MaxSupplyReached: "Sold out: the maximum supply has been minted.",
  ERC20InsufficientAllowance: "Approve more tokens and try again.",
  ERC20InsufficientBalance: "Your wallet doesn't hold enough tokens.",
  ERC721InsufficientApproval: "The marketplace approval was revoked.",
  ERC1155MissingApprovalForAll: "The marketplace approval was revoked.",
  OwnableUnauthorizedAccount: "Only the owner can do that.",
  NothingToWithdraw: "There is nothing to withdraw.",
  RoyaltyTooHigh: "The royalty is above the 10% maximum.",
};

/** The first 0x… revert payload found anywhere in a web3.js or wallet error. */
function revertData(error) {
  const queue = [error];
  while (queue.length > 0) {
    const item = queue.shift();
    if (!item) continue;
    if (typeof item === "string") {
      const match = item.match(/0x[0-9a-fA-F]{8,}/);
      if (match && match[0].length % 2 === 0) return match[0];
      try {
        queue.push(JSON.parse(item.slice(item.indexOf("{"))));
      } catch (e) {}
      continue;
    }
    if (typeof item === "object") {
      for (const key of ["data", "error", "originalError", "message", "cause"]) {
        if (item[key] !== undefined) queue.push(item[key]);
      }
    }
  }
  return undefined;
}

/** The contract error a transaction reverted with, e.g. `{ name: "PriceMismatch", message }`, or `null`. */
export const decodeContractError = (error) => {
  const data = revertData(error);
  if (!data) return null;
  try {
    const parsed = contractErrors.parseError(data);
    return { name: parsed.name, message: MESSAGES[parsed.name] || parsed.name };
  } catch (e) {
    return null;
  }
};

/** A message to show the user for a failed transaction. */
export const errorMessage = (error, fallback = "Transaction failed") => {
  if (error && error.code === 4001) return "Transaction rejected in the wallet.";
  const decoded = decodeContractError(error);
  return decoded ? decoded.message : fallback;
};
